import * as React from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * FeatureCard — shared enterprise navigation/action tile.
 *
 * Visual language mirrors the Showcase ("Calculation Modules & Platform Tools")
 * section so every page that exposes navigation options or feature highlights
 * uses the same look: gradient surface, hover lift, top accent line, tinted icon box.
 *
 * Two modes:
 *   · Static          — pass nothing for onClick/href; renders as a plain card.
 *   · Action / Link   — pass `onClick` (button) or `href` (anchor); the card becomes
 *                       clickable, shows a chevron, and gets the full interactive hover.
 */

type LucideIcon = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

export interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  desc?: string;
  /** Accent colour applied to the icon tile and top hover line. Defaults to brand gold. */
  color?: string;
  /** Click handler — when provided, the card is rendered as a button. */
  onClick?: () => void;
  /** Optional anchor target — renders as <a> instead of button. */
  href?: string;
  /** Extra badge / status content (e.g., "Enabled", "Coming soon") shown next to title. */
  badge?: React.ReactNode;
  /** Right-side trailing element (e.g., chevron override, custom action). */
  trailing?: React.ReactNode;
  /** Children rendered below the description (e.g., chips, inline meta). */
  children?: React.ReactNode;
  /** Visual variant: 'default' (showcase look) | 'compact' (denser, no description). */
  variant?: 'default' | 'compact';
  /** Disable the interactive affordance. */
  disabled?: boolean;
  className?: string;
}

export default function FeatureCard({
  icon: Icon,
  title,
  desc,
  color = 'var(--rb-brand)',
  onClick,
  href,
  badge,
  trailing,
  children,
  variant = 'default',
  disabled,
  className = '',
}: FeatureCardProps) {
  const interactive = Boolean(onClick || href) && !disabled;
  const isCompact = variant === 'compact';

  const inner = (
    <>
      {/* Top accent line — visible on hover when interactive */}
      <span
        className="rb-feature-card__line"
        aria-hidden
        style={{ background: `linear-gradient(to right, transparent, ${color}66, transparent)` }}
      />

      <div className="flex items-start gap-3">
        <div
          className={`rb-feature-card__icon ${isCompact ? 'rb-feature-card__icon--sm' : ''}`}
          style={{ background: `${color}15`, borderColor: `${color}30`, color }}
        >
          <Icon className={isCompact ? 'w-5 h-5' : 'w-6 h-6'} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 flex-wrap">
            <h3 className={isCompact ? 'text-sm font-bold text-slate-100' : 'text-base font-bold text-white'}>
              {title}
            </h3>
            {badge}
          </div>
          {desc && (
            <p className={isCompact ? 'text-xs text-slate-400 leading-snug mt-1' : 'text-sm text-slate-400 leading-relaxed mt-2'}>
              {desc}
            </p>
          )}
          {children && <div className="mt-3">{children}</div>}
        </div>

        {interactive && (
          trailing ?? (
            <ChevronRight
              className="rb-feature-card__chev shrink-0 mt-1"
              style={{ color: `${color}aa` }}
              aria-hidden
            />
          )
        )}
      </div>
    </>
  );

  const classes = [
    'rb-feature-card',
    interactive ? 'rb-feature-card--interactive' : '',
    disabled ? 'rb-feature-card--disabled' : '',
    isCompact ? 'rb-feature-card--compact' : '',
    className,
  ].filter(Boolean).join(' ');

  if (href && !disabled) {
    return (
      <a
        href={href}
        className={classes}
        style={{ ['--rb-fc-accent' as string]: color }}
      >
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={classes}
        style={{ ['--rb-fc-accent' as string]: color }}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className={classes} style={{ ['--rb-fc-accent' as string]: color }}>
      {inner}
    </div>
  );
}
